import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

import { useShareGuestPassViaEmailMutation } from "@/hooks/guestPasses";
import { GuestPassesQueryKey } from "@/hooks/useHomeData";

import ErrorText from "../ErrorText";
import FormField from "../FormField";
import FormFields from "../FormFields";
import Header from "../Header";
import Icon from "../Icon";
import { Input } from "../Input";
import Label from "../Label";
import Loader from "../Loader";
import { useNotification } from "../Notification";
import { getDaysTillExpiry, getErrorMessage, GuestPass } from ".";
import GuestPassRestrictionsAndFAQs from "./GuestPassRestictionsAndFAQs";

type Props = {
  pass: GuestPass;
  onComplete: () => void;
};

const schema = z
  .object({
    firstName: z.string().min(1, { message: "First Name is required" }),
    lastName: z.string().min(1, { message: "Last Name is required" }),
    email: z.string().min(1, { message: "Email is required" }).email({
      message: "Must be a valid email",
    }),
    emailconfirm: z.string().min(1, { message: "Email is required" }).email({
      message: "Must be a valid email",
    }),
    parentCouponCodeId: z.number(),
  })
  .refine((data) => data.email === data.emailconfirm, {
    message: "Emails must match",
    path: ["emailconfirm"],
  });

type Schema = z.infer<typeof schema>;

const inputClasses =
  "mt-2 h-14 rounded border-green-tea bg-child-of-light pt-8 text-sm";
const labelClasses = "absolute ml-3 mt-4 text-sm font-normal text-grey-blue";

export default function SharePassByEmail({ pass, onComplete }: Props) {
  const notify = useNotification();
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    getValues,
    formState: { errors, isValid },
  } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      emailconfirm: "",
      parentCouponCodeId: pass.promoCodeId,
    },
    mode: "onBlur",
  });

  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const shareGuestPassViaEmailMutation = useShareGuestPassViaEmailMutation();
  const onSubmit = handleSubmit((data) => {
    shareGuestPassViaEmailMutation.mutate(data, {
      onSuccess: (result) => {
        if (result.sucId) {
          setShowSuccess(true);
          void queryClient.invalidateQueries({
            queryKey: [GuestPassesQueryKey],
          });
        }
      },
      onError: (error) => {
        notify(getErrorMessage(error.message), "error");
      },
    });
  });

  const sendDisabled = !isValid || shareGuestPassViaEmailMutation.isPending;
  const expiresInDays = pass.expiresDate
    ? `Expires in ${getDaysTillExpiry(pass.expiresDate)}`
    : "";

  return (
    <>
      {!showConfirm && !showSuccess && (
        <>
          <form className="mb-8">
            <Header className="my-4 text-xl text-clear-sky">
              Share Your Guest Pass
            </Header>
            <div className="mb-6 flex justify-end">
              <span className="justify-between font-['Open_Sans'] text-sm font-normal leading-none text-clear-sky">
                {expiresInDays}
              </span>
            </div>
            <FormFields className="mb-4">
              <FormField>
                <Label htmlFor="firstName" className={labelClasses}>
                  Guest First Name
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  className={inputClasses}
                  {...register("firstName")}
                />
                {errors.firstName && (
                  <ErrorText>{errors.firstName.message}</ErrorText>
                )}
              </FormField>
              <FormField>
                <Label htmlFor="lastName" className={labelClasses}>
                  Guest Last Name
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  className={inputClasses}
                  {...register("lastName")}
                />
                {errors.lastName && (
                  <ErrorText>{errors.lastName.message}</ErrorText>
                )}
              </FormField>
              <FormField>
                <Label htmlFor="email" className={labelClasses}>
                  Guest Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  className={inputClasses}
                  {...register("email")}
                />
                {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
              </FormField>
              <FormField>
                <Label htmlFor="emailconfirm" className={labelClasses}>
                  Confirm Guest Email
                </Label>
                <Input
                  id="emailconfirm"
                  type="email"
                  className={inputClasses}
                  {...register("emailconfirm")}
                />
                {errors.emailconfirm && (
                  <ErrorText>{errors.emailconfirm.message}</ErrorText>
                )}
              </FormField>
              <div className="mb-6">
                <button
                  onClick={() => setShowConfirm(true)}
                  disabled={sendDisabled}
                  className={twMerge(
                    "py-2 px-4 flex justify-self-end text-sm text-clear-sky rounded-xl",
                    sendDisabled ? "bg-light-grey" : "bg-teal-700",
                  )}
                >
                  <Icon name="envelope" className="mr-4 text-clear-sky" />
                  Send
                </button>
              </div>
            </FormFields>
          </form>
          <GuestPassRestrictionsAndFAQs pass={pass} />
        </>
      )}
      {showConfirm && !showSuccess && (
        <>
          <div>You are about to send this pass to:</div>
          <span>Email:&nbsp;</span>
          <span>{getValues("email")}</span>
          <div className="mt-4 flex justify-end space-x-8">
            <button
              onClick={() => setShowConfirm(false)}
              className="flex justify-self-end rounded-xl bg-white px-4 py-2 text-sm text-deep-blue"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={shareGuestPassViaEmailMutation.isPending}
              className="flex justify-self-end rounded-xl bg-teal-700 px-4 py-2 text-sm text-clear-sky"
            >
              <Icon name="envelope" className="mr-4 text-clear-sky" />
              Send
            </button>
          </div>
          {shareGuestPassViaEmailMutation.isPending && <Loader />}
        </>
      )}
      {showSuccess && (
        <>
          <div>Guess Pass Sent!</div>
          <span className="text-xs">
            You have successfully shared a Guest Pass.
          </span>
          <div className="mt-4 flex justify-end space-x-8">
            <button
              onClick={() => onComplete()}
              className="flex justify-self-end rounded-xl bg-teal-700 px-6 py-2 text-sm text-clear-sky"
            >
              Done
            </button>
          </div>
        </>
      )}
    </>
  );
}
